import { useEffect } from "react"
import { useQueryState } from "nuqs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faGear } from "@fortawesome/free-solid-svg-icons"
import usePagination from "../../hooks/usePagination"
import {
  useCustomerSuccessCenters,
  useCustomerSuccessOverviewCounts,
} from "../../hooks/useCustomerSuccessDeals"
import OverviewStats from "./components/OverviewStats"
import CenterToolbar, { ALL } from "./components/CenterToolbar"
import CentersList from "./components/CentersList"

const CapPill = ({ studyLimit, isLoading, isError }) => {
  if (isLoading) {
    return (
      <span className="inline-block h-[30px] w-64 animate-pulse rounded-full bg-gray-100" />
    )
  }
  if (isError || !Number.isFinite(studyLimit)) return null

  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-xs text-gray-700">
      <FontAwesomeIcon className="h-3 w-3 text-gray-400" icon={faGear} />
      Auto-tracking the first <b className="text-gray-900">{studyLimit}</b>{" "}
      cases per new center
    </span>
  )
}

const CustomerSuccess = () => {
  const { limit, onPaginationChange, page, pagination } = usePagination()

  const [centerName, setCenterName] = useQueryState("center", {
    defaultValue: "",
  })
  const [status, setStatus] = useQueryState("status", { defaultValue: ALL })

  const filters = {
    centerName: centerName || undefined,
    status: status === ALL ? undefined : status,
  }

  // Real (non-infinite) pagination — a filter change must snap back to page
  // 1, otherwise sitting on page 3 and applying a filter can silently render
  // an empty list with no explanation.
  useEffect(() => {
    onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerName, status])

  const {
    data: { data = [], total = 0 } = {},
    isLoading,
    isError,
    error,
  } = useCustomerSuccessCenters({ page, limit, filters })

  const pageCount = total ? Math.ceil(total / limit) : 0

  const overview = useCustomerSuccessOverviewCounts({})

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl font-extrabold text-primary">
          Customer Success
        </h1>
        <CapPill
          isError={overview.centers.isError}
          isLoading={overview.centers.isLoading}
          studyLimit={overview.centers.studyLimit}
        />
      </div>

      <OverviewStats
        centers={overview.centers}
        csatCounts={overview.csatCounts}
        grandTotal={overview.grandTotal}
        trackingCounts={overview.trackingCounts}
      />

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-gray-900">By center</h2>
          <p className="mt-0.5 text-xs text-gray-400">
            Each new center and how far its tracked cases have progressed. Open
            a center to see individual cases.
          </p>
        </div>

        <CenterToolbar
          centerName={centerName}
          setCenterName={setCenterName}
          setStatus={setStatus}
          status={status}
        />

        <CentersList
          data={data}
          error={error}
          isError={isError}
          isLoading={isLoading}
          pageCount={pageCount}
          pagination={pagination}
          studyLimit={overview.centers.studyLimit}
          total={total}
          onPaginationChange={onPaginationChange}
        />
      </div>
    </div>
  )
}

export default CustomerSuccess
